/**
 * EmployeeHome — Modern Employee Workspace Dashboard
 *
 * Productivity-focused home screen for workers (non-admin users). Replaces the
 * minimal worker branch of Overview.js with a Deel/Rippling-style 2-column
 * layout: hero with live status & shift progress, quick actions, attendance
 * widget with real check-in/out wiring, weekly schedule preview, leave balance,
 * recent requests, activity feed and upcoming leave.
 *
 * Data sources (all already wired):
 *   - presenceService          → today's check-in / breaks / check-out events
 *   - apiService('holidays')   → my leave history, balance, upcoming, requests
 *   - apiService('settings')   → company AnnualLeaveAllowance
 *   - apiService('workers')    → my worker record (for shift, if available)
 *
 * Layout breakpoints:
 *   xs: single column, stacked
 *   md: 2-column (8 / 4) on dashboard sections
 *   lg+: maxWidth 1600 centered, breathable spacing
 */
import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box, Stack, Grid, Card, Typography, Chip, Button,
  Skeleton, useTheme, alpha,
  Snackbar, Alert,
} from '@mui/material';
import {
  Clock, LogIn, LogOut, Coffee, Play, QrCode, Calendar, FileEdit,
  CheckCircle2, AlertTriangle, Hourglass, ChevronRight,
  Plane, Activity, Sun, Moon, Cloud,
  CalendarPlus, Timer, Stethoscope, RotateCcw, CalendarClock, Layers, Wallet,
} from 'lucide-react';

import globals from '../../services/globals';
import apiService from '../../services/apiServices';
import presenceService from '../../services/presenceService';
import WorkdayTimeline from '../common/WorkdayTimeline';
import StatusChip from '../common/StatusChip';

// ── Constants & helpers ─────────────────────────────────────────────────────

const DEFAULT_SHIFT = { start_time: '09:00', end_time: '17:00', break_minutes: 60 };

const pad2 = (n) => String(n).padStart(2, '0');
const todayISO = () => new Date().toISOString().slice(0, 10);

const fmtTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d) ? '—' : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const fmtHM = (mins) => {
  if (mins == null || isNaN(mins)) return '0h 0m';
  const m = Math.max(0, Math.round(mins));
  return `${Math.floor(m / 60)}h ${m % 60}m`;
};

const fmtClock = (ms) => {
  const total = Math.max(0, Math.floor(ms / 1000));
  return `${pad2(Math.floor(total / 3600))}:${pad2(Math.floor((total % 3600) / 60))}:${pad2(total % 60)}`;
};

// Re-implementation of MyAttendancePanel's eventsToSession to keep this file
// self-contained and avoid cross-module coupling on a private helper.
function eventsToSession(events = [], dateStr = todayISO()) {
  const dayEvents = (events || [])
    .filter((e) => {
      const at = new Date(e.At || e.at);
      return !isNaN(at) && at.toISOString().slice(0, 10) === dateStr;
    })
    .sort((a, b) => new Date(a.At || a.at) - new Date(b.At || b.at));

  const session = { check_in: null, check_out: null, breaks: [] };
  dayEvents.forEach((e) => {
    const type = String(e.Type || e.type || '').toLowerCase().replace(/-/g, '_');
    const at = new Date(e.At || e.at).toISOString();
    if (type === 'checkin' || type === 'check_in' || type === 'in') {
      if (!session.check_in) session.check_in = at;
      if (session.check_out) session.check_out = null;
    } else if (type === 'checkout' || type === 'check_out' || type === 'out') {
      session.check_out = at;
    } else if (
      type === 'break_start' || type === 'lunch_start' ||
      type === 'short_break_start' || type === 'meeting_start'
    ) {
      const t = type === 'lunch_start' ? 'lunch'
              : type === 'short_break_start' ? 'short'
              : type === 'meeting_start' ? 'meeting'
              : 'break';
      session.breaks.push({ start: at, type: t });
    } else if (
      type === 'break_end' || type === 'lunch_end' ||
      type === 'short_break_end' || type === 'meeting_end'
    ) {
      for (let i = session.breaks.length - 1; i >= 0; i--) {
        if (!session.breaks[i].end) { session.breaks[i].end = at; break; }
      }
    }
  });
  return { session, events: dayEvents };
}

const greetingKey = () => {
  const h = new Date().getHours();
  if (h < 12) return 'morning';
  if (h < 18) return 'afternoon';
  return 'evening';
};

const GreetingIcon = ({ k }) =>
  k === 'morning' ? <Sun size={16} /> :
  k === 'afternoon' ? <Cloud size={16} /> :
  <Moon size={16} />;

// ── Small UI atoms ──────────────────────────────────────────────────────────

function SectionCard({ title, icon: Icon, action, children, sx, dense }) {
  return (
    <Card sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', ...sx }}>
      {title && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2, py: dense ? 1.25 : 1.5, borderBottom: 1, borderColor: 'divider' }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            {Icon && (
              <Box
                sx={{
                  width: 26, height: 26, borderRadius: 1.25,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  bgcolor: 'grey.100', color: 'text.secondary',
                }}
              >
                <Icon size={13} strokeWidth={2} />
              </Box>
            )}
            <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', letterSpacing: '-0.01em' }}>
              {title}
            </Typography>
          </Stack>
          {action}
        </Stack>
      )}
      <Box sx={{ flex: 1, minHeight: 0 }}>{children}</Box>
    </Card>
  );
}

function LivePulse({ color = '#10B981', size = 8 }) {
  return (
    <Box
      sx={{
        position: 'relative',
        width: size, height: size,
        '&::before': {
          content: '""', position: 'absolute', inset: 0,
          borderRadius: '50%', bgcolor: color,
          animation: 'pulseDot 1.6s ease-out infinite',
          opacity: 0.6,
        },
        '&::after': {
          content: '""', position: 'absolute', inset: 0,
          borderRadius: '50%', bgcolor: color,
        },
        '@keyframes pulseDot': {
          '0%':   { transform: 'scale(1)',   opacity: 0.6 },
          '100%': { transform: 'scale(2.4)', opacity: 0 },
        },
      }}
    />
  );
}

// ── Hero ────────────────────────────────────────────────────────────────────

function Hero({
  firstName, status, shift, liveClock,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const greet = greetingKey();
  const statusMap = {
    not_started: { label: t('empHome.status.notStarted'), color: theme.palette.grey[400], soft: theme.palette.grey[100] },
    working:     { label: t('empHome.status.working'),    color: theme.palette.success.main, soft: theme.palette.success.soft },
    on_break:    { label: t('empHome.status.onBreak'),    color: theme.palette.warning.main, soft: theme.palette.warning.soft },
    finished:    { label: t('empHome.status.finished'),   color: theme.palette.grey[500], soft: theme.palette.grey[100] },
  };
  const s = statusMap[status] || statusMap.not_started;

  return (
    <Card
      sx={{
        p: { xs: 2.25, md: 3 },
        position: 'relative',
        overflow: 'hidden',
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 60%, transparent 100%)`,
        borderColor: alpha(theme.palette.primary.main, 0.14),
      }}
    >
      {/* Decorative blobs */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute', top: -60, right: -60, width: 220, height: 220,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.18)} 0%, transparent 70%)`,
          pointerEvents: 'none',
        }}
      />

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 2, md: 3 }} alignItems={{ md: 'center' }} sx={{ position: 'relative' }}>
        {/* Left: greeting & status */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ color: 'text.secondary', mb: 0.75 }}>
            <GreetingIcon k={greet} />
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, letterSpacing: '0.02em', textTransform: 'uppercase' }}>
              {t(`empHome.greet.${greet}`)}
            </Typography>
          </Stack>

          <Typography
            sx={{
              fontWeight: 700,
              fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
              letterSpacing: '-0.025em',
              lineHeight: 1.1,
              color: 'text.primary',
              mb: 1,
            }}
          >
            {firstName
              ? t('empHome.hello', { name: firstName, defaultValue: `Hello, ${firstName}` })
              : t('empHome.helloFallback')}
          </Typography>

          <Stack direction="row" flexWrap="wrap" alignItems="center" spacing={1.25} rowGap={1}>
            {/* Status — icon + label + tone, so it stays legible without colour.
                The pulse is an extra live signal, never the only one. */}
            <Stack direction="row" alignItems="center" spacing={0.875}>
              <StatusChip status={status} label={s.label} />
              {status === 'working' && <LivePulse color={s.color} />}
            </Stack>

            {/* Shift info */}
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: 'text.secondary' }}>
              <Clock size={13} />
              <Typography sx={{ fontSize: '0.8125rem', fontWeight: 500 }}>
                {t('empHome.todaysShift')}:&nbsp;
                <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  {shift.start_time} → {shift.end_time}
                </Box>
              </Typography>
            </Stack>
          </Stack>
        </Box>

        {/* Right: live clock */}
        <Stack
          alignItems={{ xs: 'flex-start', md: 'flex-end' }}
          spacing={0.5}
          sx={{ minWidth: { md: 180 } }}
        >
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.secondary' }}>
            {status === 'working' ? t('empHome.liveSession') : t('empHome.now')}
          </Typography>
          <Typography
            sx={{
              fontVariantNumeric: 'tabular-nums',
              fontWeight: 700,
              fontSize: { xs: '1.75rem', md: '2rem' },
              letterSpacing: '-0.02em',
              lineHeight: 1,
              color: status === 'working' ? 'success.dark' : 'text.primary',
            }}
          >
            {liveClock}
          </Typography>
        </Stack>
      </Stack>
    </Card>
  );
}

// ── Quick Actions row ───────────────────────────────────────────────────────

function QuickActionCard({ icon: Icon, label, sub, to, onClick, accent = 'primary', variant }) {
  const theme = useTheme();
  const isPrimary = variant === 'primary';
  const isDestructive = variant === 'destructive';

  const surface = isPrimary
    ? { bg: theme.palette.primary.main, fg: '#fff', sub: 'rgba(255,255,255,0.78)' }
    : isDestructive
    ? { bg: theme.palette.grey[900], fg: '#fff', sub: 'rgba(255,255,255,0.7)' }
    : { bg: theme.palette.background.paper, fg: theme.palette.text.primary, sub: theme.palette.text.secondary };

  const inner = (
    <Box
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.25,
        p: 1.5, borderRadius: 2.5,
        bgcolor: surface.bg,
        color: surface.fg,
        border: 1,
        borderColor: isPrimary || isDestructive ? 'transparent' : 'divider',
        cursor: 'pointer',
        height: '100%',
        transition: 'transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease',
        boxShadow: isPrimary
          ? `0 6px 16px ${alpha(theme.palette.primary.main, 0.3)}`
          : '0 1px 2px rgba(17,24,39,0.04)',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: isPrimary
            ? `0 10px 22px ${alpha(theme.palette.primary.main, 0.38)}`
            : '0 6px 14px rgba(17,24,39,0.08)',
          borderColor: isPrimary || isDestructive ? 'transparent' : theme.palette.grey[300],
        },
      }}
    >
      <Box
        sx={{
          width: 34, height: 34, borderRadius: 1.5, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: isPrimary || isDestructive
            ? 'rgba(255,255,255,0.18)'
            : theme.palette[accent]?.soft || theme.palette.grey[100],
          color: isPrimary || isDestructive
            ? '#fff'
            : theme.palette[accent]?.main || theme.palette.text.primary,
        }}
      >
        <Icon size={16} strokeWidth={2} />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontSize: '0.825rem', fontWeight: 600, color: 'inherit', lineHeight: 1.2 }}>
          {label}
        </Typography>
        {sub && (
          <Typography sx={{ fontSize: '0.7rem', color: surface.sub, mt: 0.125, lineHeight: 1.2 }}>
            {sub}
          </Typography>
        )}
      </Box>
    </Box>
  );

  if (onClick) {
    return <Box onClick={onClick} sx={{ height: '100%' }}>{inner}</Box>;
  }
  return (
    <Box component={Link} to={to} sx={{ textDecoration: 'none', display: 'block', height: '100%' }}>
      {inner}
    </Box>
  );
}

// ── Live Attendance widget ──────────────────────────────────────────────────

function AttendanceWidget({
  status, session, workedMin, breakMin, expectedCheckout, shift,
  onCheckIn, onCheckOut, onBreak, onResume, acting, loading,
}) {
  const { t } = useTranslation();
  const theme = useTheme();

  const primaryAction = status === 'not_started' ? {
    icon: LogIn,    label: t('empHome.actions.checkIn'),
    color: theme.palette.primary.main, onClick: onCheckIn,
  } : status === 'on_break' ? {
    icon: Play,     label: t('empHome.actions.resume'),
    color: theme.palette.primary.main, onClick: onResume,
  } : status === 'working' ? {
    icon: LogOut,   label: t('empHome.actions.checkOut'),
    color: theme.palette.grey[900], onClick: onCheckOut,
  } : null;

  const secondaryAction = status === 'working' ? {
    icon: Coffee, label: t('empHome.actions.startBreak'), onClick: onBreak,
  } : null;

  const tiles = [
    { label: t('empHome.tiles.checkIn'),       value: fmtTime(session.check_in),  icon: LogIn,   accent: 'primary' },
    { label: t('empHome.tiles.worked'),        value: fmtHM(workedMin),           icon: Timer,   accent: 'success' },
    { label: t('empHome.tiles.break'),         value: fmtHM(breakMin),            icon: Coffee,  accent: 'warning' },
    { label: t('empHome.tiles.expectedEnd'),   value: expectedCheckout,           icon: LogOut,  accent: 'info' },
  ];

  return (
    <SectionCard
      title={t('empHome.sections.liveAttendance')}
      icon={Activity}
      action={
        <StatusChip
          status={status}
          size="sm"
          label={t(`empHome.status.${status === 'not_started' ? 'notStarted' : status === 'on_break' ? 'onBreak' : status}`)}
        />
      }
    >
      <Box sx={{ p: { xs: 1.75, md: 2 } }}>
        <Grid container spacing={1.25}>
          {tiles.map((tile) => (
            <Grid item xs={6} sm={3} key={tile.label}>
              <Box
                sx={{
                  p: 1.5, borderRadius: 2, height: '100%',
                  bgcolor: 'grey.50',
                  border: 1, borderColor: 'divider',
                }}
              >
                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.75 }}>
                  <Box
                    sx={{
                      width: 22, height: 22, borderRadius: 1,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      bgcolor: theme.palette[tile.accent].soft, color: theme.palette[tile.accent].main,
                    }}
                  >
                    <tile.icon size={11} strokeWidth={2.2} />
                  </Box>
                  <Typography sx={{ fontSize: '0.66rem', fontWeight: 600, color: 'text.secondary', letterSpacing: '0.03em', textTransform: 'uppercase' }}>
                    {tile.label}
                  </Typography>
                </Stack>
                <Typography
                  sx={{
                    fontWeight: 700, fontSize: '1.05rem',
                    color: 'text.primary',
                    fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.015em',
                  }}
                >
                  {loading ? <Skeleton variant="text" width={50} /> : tile.value}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Workday timeline — replaces the flat completion bar. A bar could only
            say "how far along"; this shows when the day started, where the time
            went, and when it should end. */}
        <Box sx={{ mt: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: 'text.secondary' }}>
              {t('timeline.title')}
            </Typography>
            <Typography className="tabular" sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'text.primary' }}>
              {shift.start_time} → {shift.end_time}
            </Typography>
          </Stack>
          {loading
            ? <Skeleton variant="rounded" height={56} />
            : <WorkdayTimeline session={session} shift={shift} t={t} height={36} />}
        </Box>

        {/* Actions */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 2 }}>
          {primaryAction && (
            <Button
              onClick={primaryAction.onClick}
              disabled={acting}
              startIcon={<primaryAction.icon size={15} />}
              variant="contained"
              fullWidth
              sx={{
                bgcolor: primaryAction.color,
                color: '#fff',
                fontWeight: 600,
                py: 1.1,
                '&:hover': { bgcolor: alpha(primaryAction.color, 0.86) },
                '&.Mui-disabled': { bgcolor: alpha(primaryAction.color, 0.5), color: '#fff' },
              }}
            >
              {primaryAction.label}
            </Button>
          )}
          {secondaryAction && (
            <Button
              onClick={secondaryAction.onClick}
              disabled={acting}
              startIcon={<secondaryAction.icon size={15} />}
              variant="outlined"
              fullWidth
              sx={{ py: 1.1, fontWeight: 600 }}
            >
              {secondaryAction.label}
            </Button>
          )}
          {status === 'finished' && (
            <Box
              sx={{
                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
                py: 1.25, borderRadius: 2, bgcolor: 'grey.50', border: 1, borderColor: 'divider', color: 'text.secondary',
              }}
            >
              <CheckCircle2 size={15} />
              <Typography sx={{ fontSize: '0.825rem', fontWeight: 600 }}>{t('empHome.dayCompleted')}</Typography>
            </Box>
          )}
        </Stack>
      </Box>
    </SectionCard>
  );
}

// ── My Requests ─────────────────────────────────────────────────────────────

// Canonical request state from the backend two-stage approval `Status`
// (pending_supervisor | pending_hr | supervisor_rejected | hr_rejected | approved),
// with a fallback to the legacy Approve flag for older records.
function requestState(req) {
  const s = String(req.Status || req.status || '').toLowerCase();
  if (s.endsWith('_rejected') || s === 'rejected') return 'rejected';
  if (s === 'approved' || req.Approve === true || req.Approve === 1 || req.Approve === '1') return 'approved';
  return 'pending';
}

function statusFromRequest(req, t, theme) {
  switch (requestState(req)) {
    case 'approved':
      return { label: t('empHome.requestStatus.approved'),  color: theme.palette.success.main, bg: theme.palette.success.soft, icon: CheckCircle2 };
    case 'rejected':
      return { label: t('empHome.requestStatus.rejected'),  color: theme.palette.error.main,   bg: theme.palette.error.soft,   icon: AlertTriangle };
    default:
      return { label: t('empHome.requestStatus.pending'),    color: theme.palette.warning.main, bg: theme.palette.warning.soft, icon: Hourglass };
  }
}

function RecentRequests({ requests, loading, leaveTypeLabel }) {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <SectionCard
      title={t('empHome.sections.recentRequests')}
      icon={FileEdit}
      action={
        <Button
          component={Link} to="/my-leaves" size="small" endIcon={<ChevronRight size={14} />}
          sx={{ color: 'text.secondary', '&:hover': { bgcolor: 'transparent', color: 'primary.main' } }}
        >
          {t('empHome.viewAll')}
        </Button>
      }
    >
      {loading ? (
        <Stack spacing={1.25} sx={{ p: 2 }}>
          {[1,2].map(i => <Skeleton key={i} variant="rounded" height={56} />)}
        </Stack>
      ) : requests.length === 0 ? (
        <EmptyState
          icon={CalendarPlus}
          title={t('empHome.empty.requestsTitle')}
          sub={t('empHome.empty.requestsSub')}
          action={
            <Button
              component={Link} to="/holidays/addholiday" variant="outlined" size="small"
              startIcon={<CalendarPlus size={13} />}
            >
              {t('empHome.actions.requestLeave')}
            </Button>
          }
        />
      ) : (
        <Box>
          {requests.slice(0, 4).map((r, i) => {
            const st = statusFromRequest(r, t, theme);
            const StatusIcon = st.icon;
            return (
              <Stack
                key={r.Id || i}
                direction="row" alignItems="center" justifyContent="space-between" spacing={1}
                sx={{
                  px: 2, py: 1.25,
                  borderBottom: 1, borderColor: 'divider',
                  '&:last-of-type': { borderBottom: 0 },
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'text.primary', lineHeight: 1.3 }}>
                    {leaveTypeLabel(r.HolidayType)}
                  </Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mt: 0.125 }}>
                    {fmtShortDate(r.StartDate)} → {fmtShortDate(r.EndDate)}
                    {r.holidayDays || r.HolidayDays
                      ? ` · ${r.holidayDays || r.HolidayDays} ${t('empHome.daysShort')}`
                      : ''}
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  icon={<StatusIcon size={11} style={{ marginLeft: 4 }} />}
                  label={st.label}
                  sx={{
                    height: 22, fontWeight: 600, fontSize: '0.68rem',
                    bgcolor: st.bg, color: st.color, border: 0,
                    '& .MuiChip-icon': { color: st.color },
                  }}
                />
              </Stack>
            );
          })}
        </Box>
      )}
    </SectionCard>
  );
}

// ── Upcoming leave card ─────────────────────────────────────────────────────

function UpcomingLeave({ upcoming, loading, leaveTypeLabel }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const next = upcoming[0];

  return (
    <SectionCard title={t('empHome.sections.upcomingLeave')} icon={Plane}>
      {loading ? (
        <Box sx={{ p: 2 }}><Skeleton variant="rounded" height={72} /></Box>
      ) : !next ? (
        <EmptyState
          icon={Plane}
          title={t('empHome.empty.upcomingTitle')}
          sub={t('empHome.empty.upcomingSub')}
        />
      ) : (
        <Box sx={{ p: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 48, height: 48, borderRadius: 2, flexShrink: 0,
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                bgcolor: theme.palette.primary.soft, color: theme.palette.primary.dark,
              }}
            >
              <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', lineHeight: 1 }}>
                {new Date(next.StartDate).toLocaleString([], { month: 'short' })}
              </Typography>
              <Typography sx={{ fontSize: '1.125rem', fontWeight: 700, lineHeight: 1, mt: 0.25 }}>
                {new Date(next.StartDate).getDate()}
              </Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'text.primary' }}>
                {leaveTypeLabel(next.HolidayType)}
              </Typography>
              <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mt: 0.125 }}>
                {fmtShortDate(next.StartDate)} → {fmtShortDate(next.EndDate)}
              </Typography>
              <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled', mt: 0.25 }}>
                {t('empHome.daysCount', { count: next.holidayDays || next.HolidayDays || 0 })}
              </Typography>
            </Box>
          </Stack>
          {upcoming.length > 1 && (
            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mt: 1.25, fontWeight: 500 }}>
              + {upcoming.length - 1} {t('empHome.moreUpcoming')}
            </Typography>
          )}
        </Box>
      )}
    </SectionCard>
  );
}

// ── Leave balance ───────────────────────────────────────────────────────────

// Format a day count like the source system: up to 2 decimals, trailing zeros trimmed.
function fmtDays(n) {
  const r = Math.round((Number(n) || 0) * 100) / 100;
  return r.toFixed(2).replace(/\.?0+$/, '');
}

function LeaveBalance({ stats, loading }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const {
    annual = 0, carriedOver = 0, usedAnnual = 0, usedCarried = 0,
    totalUsed = 0, medicalUsed = 0, unpaidUsed = 0,
    pending = 0, remaining = 0, capacity = 0,
  } = stats || {};

  const usedPct = capacity > 0 ? Math.min(100, Math.round((totalUsed / capacity) * 100)) : 0;
  const pendingPct = capacity > 0 ? Math.min(100 - usedPct, Math.round((pending / capacity) * 100)) : 0;

  // Carried-over leave conventionally expires 30 June of the current year.
  const vd = new Date(new Date().getFullYear(), 5, 30);
  const validUntil = `${String(vd.getDate()).padStart(2, '0')}.${String(vd.getMonth() + 1).padStart(2, '0')}.${vd.getFullYear()}`;
  const hasCarried = carriedOver > 0;

  // Full leave breakdown — mirrors the source HR system. Carried-over figures
  // only appear when the worker actually has carried days.
  const tiles = [
    { key: 'capacity',    label: t('empHome.leaveBreakdown.totalAllowance'), value: capacity,    icon: Layers,        color: theme.palette.primary.main },
    { key: 'annual',      label: t('empHome.leaveBreakdown.annual'),         value: annual,      icon: Sun,           color: theme.palette.primary.main },
    hasCarried && { key: 'carried', label: t('empHome.leaveBreakdown.carried'), sub: t('empHome.leaveBreakdown.validUntil', { date: validUntil }), value: carriedOver, icon: RotateCcw, color: theme.palette.info.main },
    { key: 'usedAnnual',  label: t('empHome.leaveBreakdown.usedAnnual'),     value: usedAnnual,  icon: Plane,         color: theme.palette.primary.main },
    hasCarried && { key: 'usedCarried', label: t('empHome.leaveBreakdown.usedCarried'), value: usedCarried, icon: RotateCcw, color: theme.palette.info.main },
    { key: 'totalUsed',   label: t('empHome.leaveBreakdown.totalUsed'),      value: totalUsed,   icon: CalendarClock, color: theme.palette.text.secondary },
    { key: 'remaining',   label: t('empHome.leaveBreakdown.remaining'),      value: remaining,   icon: CheckCircle2,  color: theme.palette.success.main, emphasis: true },
    { key: 'medicalUsed', label: t('empHome.leaveBreakdown.medicalUsed'),    value: medicalUsed, icon: Stethoscope,   color: theme.palette.error.main },
    { key: 'unpaidUsed',  label: t('empHome.leaveBreakdown.unpaidUsed'),     value: unpaidUsed,  icon: Wallet,        color: theme.palette.warning.main },
  ].filter(Boolean);

  return (
    <SectionCard title={t('empHome.sections.leaveBalance')} icon={Plane}>
      <Box sx={{ p: 2 }}>
        {/* Used vs total — slim context bar above the tiles */}
        <Box
          sx={{
            display: 'flex', height: 6, borderRadius: 999, overflow: 'hidden',
            bgcolor: 'grey.100', mb: 1.75,
          }}
        >
          <Box sx={{ width: `${usedPct}%`, bgcolor: 'primary.main' }} />
          <Box sx={{ width: `${pendingPct}%`, bgcolor: alpha(theme.palette.warning.main, 0.7) }} />
        </Box>

        {/* Stat tiles — reference card grid */}
        <Grid container spacing={1.25}>
          {tiles.map((tile) => (
            <Grid item xs={6} sm={4} key={tile.key}>
              <LeaveTile {...tile} loading={loading} />
            </Grid>
          ))}
        </Grid>

        {pending > 0 && (
          <Typography sx={{ mt: 1.5, fontSize: '0.7rem', color: 'warning.main', fontWeight: 500 }}>
            {t('empHome.leaveBreakdown.pendingNote', { count: fmtDays(pending) })}
          </Typography>
        )}

        <Button
          component={Link} to="/holidays/addholiday"
          variant="contained"
          fullWidth
          startIcon={<CalendarPlus size={14} />}
          sx={{ mt: 1.75, py: 0.875 }}
        >
          {t('empHome.actions.requestLeave')}
        </Button>
      </Box>
    </SectionCard>
  );
}

function LeaveTile({ icon: Icon, color, label, sub, value, loading, emphasis }) {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        position: 'relative', height: '100%',
        p: 1.5, borderRadius: 2,
        border: 1,
        borderColor: emphasis ? alpha(color, 0.5) : 'divider',
        bgcolor: emphasis ? alpha(color, 0.06) : 'background.paper',
      }}
    >
      {/* Tinted corner icon, like the reference cards */}
      <Box
        sx={{
          position: 'absolute', top: 10, right: 10,
          width: 28, height: 28, borderRadius: 1.5,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: alpha(color, 0.12), color,
        }}
      >
        <Icon size={15} strokeWidth={2} />
      </Box>

      {/* Big number + days unit */}
      <Stack direction="row" alignItems="flex-end" spacing={0.375} sx={{ pr: 4 }}>
        <Typography
          sx={{
            fontWeight: 800, fontSize: '1.5rem', lineHeight: 1,
            color: emphasis ? color : 'text.primary',
            fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em',
          }}
        >
          {loading ? <Skeleton width={36} /> : fmtDays(value)}
        </Typography>
        <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', pb: 0.25 }}>
          {t('empHome.daysShort')}
        </Typography>
      </Stack>

      {/* Uppercase label */}
      <Typography
        sx={{
          mt: 0.75, fontSize: '0.66rem', fontWeight: 700,
          letterSpacing: '0.04em', textTransform: 'uppercase',
          color: 'text.secondary', lineHeight: 1.3,
        }}
      >
        {label}
      </Typography>
      {sub && (
        <Typography sx={{ fontSize: '0.62rem', color: 'text.disabled', lineHeight: 1.3, mt: 0.25 }}>
          {sub}
        </Typography>
      )}
    </Box>
  );
}

// ── Empty state ─────────────────────────────────────────────────────────────

function EmptyState({ icon: Icon, title, sub, action }) {
  return (
    <Box sx={{ py: 3.5, px: 2, textAlign: 'center' }}>
      <Box
        sx={{
          width: 40, height: 40, borderRadius: 2,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: 'grey.100', color: 'text.disabled',
          mb: 1.25,
        }}
      >
        <Icon size={18} strokeWidth={1.8} />
      </Box>
      <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'text.primary' }}>{title}</Typography>
      {sub && (
        <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mt: 0.375, maxWidth: 240, mx: 'auto' }}>
          {sub}
        </Typography>
      )}
      {action && <Box sx={{ mt: 1.5 }}>{action}</Box>}
    </Box>
  );
}

// ── Date helpers ────────────────────────────────────────────────────────────

function fmtShortDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  return d.toLocaleDateString([], { day: '2-digit', month: 'short' });
}

// ── Main component ─────────────────────────────────────────────────────────

export default function EmployeeHome() {
  const { t } = useTranslation();
  const theme = useTheme();

  const _currentUser = globals().getLogedInUser();
  const _myData = _currentUser?.data || _currentUser || {};
  // worker_id lives either at the top level of the session (SignIn) or inside .data
  // (loginService) — check both. Do NOT fall back to the user id, which is unrelated.
  const _myWorkerId = String(
    _currentUser?.worker_id ?? _currentUser?.WorkerId ??
    _myData.worker_id ?? _myData.WorkerId ?? ''
  );
  const firstName = _myData.first_name || _myData.FirstName || _myData.firstname || _myData.name || '';

  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [todayEvents, setTodayEvents] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [annual, setAnnual] = useState(20);
  const [carriedOver, setCarriedOver] = useState(0);
  const [toast, setToast] = useState({ open: false, msg: '', severity: 'success' });
  const [now, setNow] = useState(new Date());
  const shift = DEFAULT_SHIFT; // shift template lookup could be wired later

  // 1s tick for live clock & worked-time counter
  const tickRef = useRef();
  useEffect(() => {
    tickRef.current = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(tickRef.current);
  }, []);

  // ── Load presence + holidays ──
  const load = useCallback(async () => {
    try {
      const [today, holidaysRes, settings] = await Promise.all([
        presenceService().getDay(),
        apiService().get('holidays'),
        globals().getSettings().catch(() => null),
      ]);
      setTodayEvents(today || []);
      if (holidaysRes?.success) {
        const mine = (holidaysRes.data || []).filter(h => String(h.WorkerId) === _myWorkerId);
        setHolidays(mine);
      }
      if (settings) setAnnual(Number(settings.AnnualLeaveAllowance) || 20);

      // Carried-over leave (Bartur) — lives only in leave_balances, not in the
      // active holidays flow. Fetch it separately; fall back to 0 if unavailable.
      if (_myWorkerId) {
        try {
          const year = new Date().getFullYear();
          const lbRes = await apiService().query('getLeaveBalances', `workerId=${_myWorkerId}&year=${year}`);
          if (lbRes?.success) {
            const co = (lbRes.data || [])
              .filter(b => Number(b.LeaveTypeId) === 1)
              .reduce((s, b) => s + (Number(b.CarriedOver) || 0), 0);
            setCarriedOver(co);
          }
        } catch (_) {}
      }
    } finally {
      setLoading(false);
    }
  }, [_myWorkerId]);

  useEffect(() => { load(); }, [load]);

  // ── Today's session ──
  const { session } = useMemo(
    () => eventsToSession(todayEvents, todayISO()),
    [todayEvents]
  );

  const totalBreakMin = useMemo(() => (
    (session.breaks || []).reduce((s, b) => {
      if (!b.start) return s;
      const st = new Date(b.start).getTime();
      const en = b.end ? new Date(b.end).getTime() : now.getTime();
      return s + Math.max(0, Math.round((en - st) / 60000));
    }, 0)
  ), [session.breaks, now]);

  const workedMin = useMemo(() => {
    if (!session.check_in) return 0;
    const start = new Date(session.check_in).getTime();
    const end = session.check_out ? new Date(session.check_out).getTime() : now.getTime();
    return Math.max(0, Math.round((end - start) / 60000) - totalBreakMin);
  }, [session.check_in, session.check_out, totalBreakMin, now]);

  // Status from session
  const status = useMemo(() => {
    if (!session.check_in) return 'not_started';
    if (session.check_out) return 'finished';
    const lastBreak = session.breaks[session.breaks.length - 1];
    if (lastBreak && !lastBreak.end) return 'on_break';
    return 'working';
  }, [session]);

  // Shift maths
  const { expectedCheckout } = useMemo(() => {
    const startIso = `${todayISO()}T${shift.start_time}:00`;
    const endIso   = `${todayISO()}T${shift.end_time}:00`;
    const total = Math.max(1, Math.round((new Date(endIso) - new Date(startIso)) / 60000));
    const pct = Math.min(100, Math.max(0, Math.round((workedMin / Math.max(1, total - shift.break_minutes)) * 100)));
    const expected = session.check_in
      ? new Date(new Date(session.check_in).getTime() + total * 60000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      : fmtTime(endIso);
    return { expectedCheckout: expected };
  }, [workedMin, session.check_in, shift]);

  // Live session clock (HH:mm:ss)
  const liveClock = useMemo(() => {
    if (status === 'working' && session.check_in) {
      return fmtClock(now.getTime() - new Date(session.check_in).getTime() - totalBreakMin * 60000);
    }
    return now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  }, [status, session.check_in, now, totalBreakMin]);

  // ── Actions ──
  const fireAction = async (type, msg) => {
    if (!_myWorkerId || acting) return;
    setActing(true);
    try {
      await presenceService().addEvent({ type, workerId: _myWorkerId });
      setToast({ open: true, msg, severity: 'success' });
      setTimeout(load, 600);
    } catch (e) {
      setToast({ open: true, msg: e?.message || t('empHome.toast.error'), severity: 'error' });
    } finally {
      setActing(false);
    }
  };
  const handleCheckIn  = () => fireAction('checkin',     t('empHome.toast.checkedIn'));
  const handleCheckOut = () => fireAction('checkout',    t('empHome.toast.checkedOut'));
  const handleBreak    = () => fireAction('break_start', t('empHome.toast.breakStarted'));
  const handleResume   = () => fireAction('break_end',   t('empHome.toast.breakEnded'));

  // ── Leave stats ──
  // Allowance side (capacity) comes from settings (annual) + leave_balances
  // (carried-over). Consumption side is derived from the actual approved leave
  // records, the source of truth for what's been taken.
  const leaveStats = useMemo(() => {
    // 3 dhjetore — mbledhja/zbritja float e gjysmë-ditëve lë artefakte (2.5999…).
    const r3 = (n) => Math.round(n * 1000) / 1000;
    const sumApproved = (types) => r3(holidays
      .filter(h => h.Approve && types.includes(Number(h.HolidayType)))
      .reduce((s, h) => s + (Number(h.holidayDays || h.HolidayDays) || 0), 0));

    const usedAnnualTotal = sumApproved([1, 6]); // vjetor + me pagesë
    const medicalUsed = sumApproved([2]);        // mjekësor
    const unpaidUsed = sumApproved([4]);         // pa pagesë
    const pending = r3(holidays
      .filter(h => requestState(h) === 'pending')
      .reduce((s, h) => s + (Number(h.holidayDays || h.HolidayDays) || 0), 0));

    // Carried-over leave is consumed before the annual allowance (regional convention).
    const usedCarried = Math.min(usedAnnualTotal, carriedOver);
    const usedAnnual = r3(usedAnnualTotal - usedCarried);
    const capacity = r3(annual + carriedOver);
    const remaining = Math.max(0, r3(capacity - usedAnnualTotal));

    return {
      annual, carriedOver,
      usedAnnual, usedCarried, totalUsed: usedAnnualTotal,
      medicalUsed, unpaidUsed, pending, remaining, capacity,
      // legacy aliases (used elsewhere)
      used: usedAnnualTotal,
    };
  }, [holidays, annual, carriedOver]);

  // ── Recent requests + upcoming leave ──
  const recentRequests = useMemo(() => {
    return [...holidays]
      .sort((a, b) => new Date(b.CreatedAt || b.StartDate || 0) - new Date(a.CreatedAt || a.StartDate || 0))
      .slice(0, 6);
  }, [holidays]);

  const upcomingLeave = useMemo(() => {
    const today = todayISO();
    return holidays
      .filter(h => h.Approve && h.StartDate && new Date(h.StartDate).toISOString().slice(0, 10) >= today)
      .sort((a, b) => new Date(a.StartDate) - new Date(b.StartDate));
  }, [holidays]);

  const leaveTypeLabel = useCallback((typeId) => {
    return t(`leaveTypes.${Number(typeId) || 1}`, t('leaveTypes.1'));
  }, [t]);

  // ── Layout ──
  return (
    <Box
      sx={{
        bgcolor: 'background.default',
        minHeight: '100%',
        overflow: 'auto',
      }}
    >
      <Box
        sx={{
          maxWidth: 1600, mx: 'auto',
          px: { xs: 2, sm: 2.5, md: 3 },
          py: { xs: 2, md: 2.5 },
        }}
      >
        <Stack spacing={{ xs: 1.75, md: 2 }}>
          {/* ── Hero ── */}
          <Hero
            firstName={firstName}
            status={status}
            shift={shift}
            liveClock={liveClock}
          />

          {/* ── Quick Actions ── */}
          <Grid container spacing={{ xs: 1, md: 1.5 }}>
            <Grid item xs={12} sm={6} md={2.4}>
              <QuickActionCard
                icon={status === 'working' ? LogOut : status === 'on_break' ? Play : LogIn}
                label={
                  status === 'working' ? t('empHome.actions.checkOut') :
                  status === 'on_break' ? t('empHome.actions.resume') :
                  t('empHome.actions.checkIn')
                }
                sub={t('empHome.subs.attendance')}
                onClick={
                  status === 'working' ? handleCheckOut :
                  status === 'on_break' ? handleResume :
                  handleCheckIn
                }
                variant={status === 'working' ? 'destructive' : 'primary'}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={2.4}>
              <QuickActionCard
                icon={CalendarPlus}
                label={t('empHome.actions.requestLeave')}
                sub={t('empHome.subs.leave')}
                to="/holidays/addholiday"
                accent="info"
              />
            </Grid>
            <Grid item xs={6} sm={6} md={2.4}>
              <QuickActionCard
                icon={FileEdit}
                label={t('empHome.actions.correction')}
                sub={t('empHome.subs.correction')}
                to="/my-attendance"
                accent="warning"
              />
            </Grid>
            <Grid item xs={6} sm={6} md={2.4}>
              <QuickActionCard
                icon={QrCode}
                label={t('empHome.actions.scanQR')}
                sub={t('empHome.subs.scan')}
                to="/scan"
                accent="success"
              />
            </Grid>
            <Grid item xs={6} sm={6} md={2.4}>
              <QuickActionCard
                icon={Calendar}
                label={t('empHome.actions.viewSchedule')}
                sub={t('empHome.subs.schedule')}
                to="/work-schedules"
                accent="primary"
              />
            </Grid>
          </Grid>

          {/* ── Main grid — leave-focused ── */}
          <Grid container spacing={{ xs: 1.5, md: 2 }}>
            {/* LEFT — leave (primary focus) */}
            <Grid item xs={12} md={7}>
              <Stack spacing={{ xs: 1.5, md: 2 }}>
                <LeaveBalance
                  stats={leaveStats}
                  loading={loading}
                />
                <Grid container spacing={{ xs: 1.5, md: 2 }}>
                  <Grid item xs={12} sm={6}>
                    <UpcomingLeave
                      upcoming={upcomingLeave}
                      loading={loading}
                      leaveTypeLabel={leaveTypeLabel}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <RecentRequests
                      requests={recentRequests}
                      loading={loading}
                      leaveTypeLabel={leaveTypeLabel}
                    />
                  </Grid>
                </Grid>
              </Stack>
            </Grid>

            {/* RIGHT — attendance essentials */}
            <Grid item xs={12} md={5}>
              <Stack spacing={{ xs: 1.5, md: 2 }}>
                <AttendanceWidget
                  status={status}
                  session={session}
                  workedMin={workedMin}
                  breakMin={totalBreakMin}
                  expectedCheckout={expectedCheckout}
                  shift={shift}
                  onCheckIn={handleCheckIn}
                  onCheckOut={handleCheckOut}
                  onBreak={handleBreak}
                  onResume={handleResume}
                  acting={acting}
                  loading={loading}
                />
              </Stack>
            </Grid>
          </Grid>
        </Stack>
      </Box>

      <Snackbar
        open={toast.open}
        autoHideDuration={2400}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast.severity} variant="filled" sx={{ fontWeight: 600 }}>
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
